/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';

import { Button, IconButton } from '@coursera/cds-core';
import { ChatIcon } from '@coursera/cds-icons';

import ChatIconV2 from 'bundles/common/components/ChatIconV2';

import _t from 'i18n!nls/common';

type Props = {
  onClick: () => void;
  show?: boolean;
  altText?: string;
  /** When isInlineButton is true we display a regular button where the component is inserted, instead of a button floating on the bottom right of the page */
  isInlineButton?: boolean;
  chatButtonBottomOffsetOverride?: number;
  /** Optional overrides for the container CSS. Useful for responsive positioning. */
  containerCssOverrides?: Interpolation<Theme>;
};

const getDefaultAltText = () => _t('Chat with us');

const chatButtonAppearKeyframes = keyframes({
  from: {
    transform: 'scale(0)',
  },
  to: {
    transform: 'scale(1)',
  },
});

const floatingButtonStyles = {
  chatButtonContainer: css`
    /* Positioned to cover over top of the bottom right help button for now */
    z-index: 2000;
    position: fixed;
    right: var(--cds-spacing-150);
    bottom: var(--cds-spacing-150);
    animation: ${chatButtonAppearKeyframes} 0.2s ease-out both;
  `,

  chatButtonCustomCss: (chatButtonBottomOffsetOverride: number) => css`
    bottom: ${chatButtonBottomOffsetOverride}px;
  `,
};

const inlineButtonStyles = {
  chatButtonContainer: {},
};

/**
 * Renders a Chat icon button that will animate in from the lower right of the screen
 */
const ChatButton = ({
  onClick,
  show = true,
  altText = getDefaultAltText(),
  isInlineButton = false,
  chatButtonBottomOffsetOverride = 0,
  containerCssOverrides,
}: Props) => {
  const styles = isInlineButton ? inlineButtonStyles : floatingButtonStyles;

  // We use a show prop, instead of having the parent component do `{{show && <ChatButton />}}` because sudden removal from the component tree would make it hard to implement a disappearance animation in the future
  if (!show) {
    return null;
  }

  const chatButtonElement = isInlineButton ? (
    <Button
      variant="primary"
      onClick={onClick}
      icon={<ChatIcon title={altText} width={20} height={20} color="invert" />}
      iconPosition="after"
    >
      {_t('Start chat')}
    </Button>
  ) : (
    <IconButton
      aria-label={altText}
      tooltip={altText}
      variant="ghost"
      onClick={onClick}
      size="small"
      icon={<ChatIconV2 />}
    />
  );

  return (
    <div
      css={[
        styles.chatButtonContainer,
        chatButtonBottomOffsetOverride > 0 && floatingButtonStyles.chatButtonCustomCss(chatButtonBottomOffsetOverride),
        containerCssOverrides,
      ]}
      id="chat-button-container"
    >
      {chatButtonElement}
    </div>
  );
};

export default ChatButton;
